/**
 * TrackStatusStrip (Overview v3, decision D4)
 *
 * Replaces the Intake → Care → Recertification → Discharge bar. For the selected discipline it
 * shows what a coordinator checks every day: the prior authorization (units, end date, renewal
 * badge), the plan of care period, the last visit, the next shift and the assigned clinician.
 * Renewal wording is plain language from fixed rules (D5): "Renewal opens Nov 4", "Renew by
 * Nov 27", "Renewal late".
 */
import React from 'react';
import type { PlanOfCareSummary } from '../../../dtos/patientmr/certificationPeriod.dto';
import type { ServiceTrack } from './types';
import { DISCIPLINE_DISPLAY } from './types';
import { formatDay, paUsageText, renewalBadge, TONE_CLASSES } from './format';

interface TrackStatusStripProps {
  track: ServiceTrack;
  planOfCare?: PlanOfCareSummary | null;
}

const Cell: React.FC<{ label: string; children: React.ReactNode; testId?: string }> = ({ label, children, testId }) => (
  <div className="min-w-0" data-testid={testId}>
    <p className="text-xs text-slate-500">{label}</p>
    <div className="text-sm font-medium text-slate-800 mt-0.5 break-words">{children}</div>
  </div>
);

function planOfCareText(poc: PlanOfCareSummary | null | undefined): { text: string; warn: boolean } {
  if (!poc) return { text: 'None on file', warn: true };
  if (poc.status === 'draft') return { text: 'Draft, not signed', warn: true };
  if (poc.awaitingSignature) return { text: 'Waiting for signature', warn: true };
  if (poc.status === 'expired') return { text: `Ended ${formatDay(poc.periodEnd)}`, warn: true };
  if (poc.signed) return { text: `Signed, through ${formatDay(poc.periodEnd)}`, warn: false };
  return { text: poc.status || '—', warn: true };
}

const TrackStatusStrip: React.FC<TrackStatusStripProps> = ({ track, planOfCare }) => {
  const s = track.summary;
  const pa = s?.pa ?? null;
  const badge = renewalBadge(pa);
  const poc = planOfCareText(planOfCare);
  const name = DISCIPLINE_DISPLAY[track.discipline] || track.discipline;
  const usedPct = pa && pa.approvedUnits ? Math.min(100, Math.round(((pa.usedUnits ?? 0) / pa.approvedUnits) * 100)) : null;

  return (
    <section aria-label={`${name} status`} className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 md:p-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Cell label="Prior authorization" testId="strip-pa">
          {pa ? (
            <>
              <span>{paUsageText(pa)}</span>
              {usedPct !== null && (
                <div className="w-full bg-slate-100 rounded-full h-1.5 mt-1" aria-hidden="true">
                  <div className={`h-1.5 rounded-full ${usedPct >= 100 ? 'bg-red-500' : usedPct >= 80 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${usedPct}%` }} />
                </div>
              )}
              {pa.endDate && <span className="block text-xs text-slate-500 mt-0.5">Ends {formatDay(pa.endDate, true)}</span>}
              {badge && (
                <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${TONE_CLASSES[badge.tone]}`} data-testid="renewal-badge">{badge.text}</span>
              )}
            </>
          ) : (
            <span className="text-amber-700">None on file</span>
          )}
        </Cell>
        <Cell label="Plan of care" testId="strip-poc">
          <span className={poc.warn ? 'text-amber-700' : undefined}>{poc.text}</span>
        </Cell>
        <Cell label="Last visit" testId="strip-last-visit">
          {s?.lastVisitDate ? (
            <>
              {formatDay(s.lastVisitDate)}
              <span className="block text-xs text-slate-500">{s.visitsLast30Days} in the last 30 days</span>
            </>
          ) : 'None yet'}
        </Cell>
        <Cell label="Next shift" testId="strip-next-shift">
          {s?.nextShift ? (
            <>
              {formatDay(s.nextShift.date)}{s.nextShift.startTime ? `, ${s.nextShift.startTime}` : ''}
              {!s.nextShift.staffed && <span className="block text-xs font-medium text-red-700">No clinician assigned</span>}
            </>
          ) : 'None scheduled'}
        </Cell>
        <Cell label="Clinician" testId="strip-clinician">
          {s?.assignedClinician ? (
            <>
              {s.assignedClinician.displayName || 'Assigned'}
              {s.otherClinicianCount > 0 && <span className="block text-xs text-slate-500">+{s.otherClinicianCount} more</span>}
            </>
          ) : <span className="text-amber-700">Not assigned</span>}
        </Cell>
      </div>
    </section>
  );
};

export default TrackStatusStrip;
